import { useState } from "react";
import { toast } from "sonner";
import { marketingApi } from "@/lib/marketing";
import { Field, TextInput, TextArea, Select, PURPOSES } from "./shared";

const UTM_PRESETS = [
  { label: "LinkedIn · Organic", source: "linkedin", medium: "organic_social" },
  { label: "LinkedIn · Outreach DM", source: "linkedin", medium: "outreach" },
  { label: "Google · CPC", source: "google", medium: "cpc" },
  { label: "Instagram · Organic", source: "instagram", medium: "organic_social" },
  { label: "Email · Newsletter", source: "email", medium: "newsletter" },
];

const emptyForm = {
  name: "", destination_url: "", purpose: "outreach", offer_key: "", campaign_key: "",
  channel: "", placement: "", content_label: "", content_id: "", notes: "",
  utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "", utm_id: "",
};

export default function CreateLinkForm({ onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const applyPreset = (preset) => setForm((f) => ({ ...f, utm_source: preset.source, utm_medium: preset.medium }));

  const previewQuery = new URLSearchParams(
    Object.entries({
      utm_source: form.utm_source, utm_medium: form.utm_medium, utm_campaign: form.utm_campaign,
      utm_content: form.utm_content, utm_term: form.utm_term, utm_id: form.utm_id,
      link_id: "<generated-by-server>",
    }).filter(([, v]) => v),
  ).toString();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await marketingApi.createLink({
        name: form.name, destination_url: form.destination_url, purpose: form.purpose,
        offer_key: form.offer_key || null, campaign_key: form.campaign_key || null,
        channel: form.channel || null, placement: form.placement || null,
        content_label: form.content_label || null, content_id: form.content_id || null,
        notes: form.notes || null,
        utm: { source: form.utm_source || null, medium: form.utm_medium || null, campaign: form.utm_campaign || null, content: form.utm_content || null, term: form.utm_term || null, id: form.utm_id || null },
      });
      toast.success("Tracking link created");
      setForm(emptyForm);
      onCreated();
    } catch (e2) {
      setErr(e2.message || "Failed to create link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 bg-paper border border-ink/12 p-6" data-testid="create-link-form">
      <h2 className="font-serif text-xl text-ink mb-2">New tracking link</h2>
      <Field label="Name" hint="Internal name so you can recognize this link later. Example: LinkedIn Post – Audit – Sep 2026">
        <TextInput required value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="link-name-input" />
      </Field>
      <Field label="Destination URL" hint="The page visitors should reach after clicking the link. Example: https://interactglobally.com.br/insights/free-presentation-assessment">
        <TextInput required value={form.destination_url} onChange={(e) => set("destination_url")(e.target.value)} data-testid="link-destination-input" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Purpose" hint="What this Tracking Link is being used for. Examples: organic, outreach, email, paid, qr">
          <Select value={form.purpose} onChange={set("purpose")} options={PURPOSES} data-testid="link-purpose-select" />
        </Field>
        <Field label="Offer" hint="The offer, product, service or lead magnet promoted by this link. Example: Next Presentation Readiness Check. Leave blank if not tied to a specific offer.">
          <TextInput value={form.offer_key} onChange={(e) => set("offer_key")(e.target.value)} data-testid="link-offer-input" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Campaign" hint="Shared campaign name used to group related links. Example: audit_acquisition_2026. Use the same value across related links.">
          <TextInput value={form.campaign_key} onChange={(e) => set("campaign_key")(e.target.value)} data-testid="link-campaign-input" />
        </Field>
        <Field label="Channel" hint="Where the traffic comes from. Examples: linkedin, instagram, youtube, email, google">
          <TextInput value={form.channel} onChange={(e) => set("channel")(e.target.value)} data-testid="link-channel-input" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Placement" hint="The specific location or surface inside the channel. Examples: post, reel, bio, direct_message, newsletter, search_ad">
          <TextInput value={form.placement} onChange={(e) => set("placement")(e.target.value)} data-testid="link-placement-input" />
        </Field>
        <Field label="Content / Asset name" hint="Human-readable name of the specific post, video, email or ad, for internal recognition. Example: Board Presentation Post #03">
          <TextInput value={form.content_label} onChange={(e) => set("content_label")(e.target.value)} data-testid="link-content-label-input" />
        </Field>
      </div>
      <Field label="Content ID" hint="Optional pointer to a Content Registry item. Leave blank if no Content Registry item exists yet — do not invent one.">
        <TextInput value={form.content_id} onChange={(e) => set("content_id")(e.target.value)} data-testid="link-content-id-input" />
      </Field>
      <Field label="Notes" hint="Private internal notes about this link — never included in the public URL. Example: Used for September outreach to pharma conference speakers">
        <TextArea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="link-notes-input" />
      </Field>

      <div className="pt-2 border-t border-ink/10">
        <p className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft mb-2">UTM presets</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="utm-presets">
          {UTM_PRESETS.map((p) => (
            <button key={p.label} type="button" onClick={() => applyPreset(p)} data-testid={`utm-preset-${p.label.replace(/\W+/g, "-").toLowerCase()}`}
              className="border border-ink/20 px-3 py-1.5 font-body text-xs text-ink-soft hover:border-terracotta hover:text-terracotta transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="UTM source" hint="The traffic source. Example: linkedin. Usually auto-filled by a preset.">
            <TextInput value={form.utm_source} onChange={(e) => set("utm_source")(e.target.value)} data-testid="link-utm-source-input" />
          </Field>
          <Field label="UTM medium" hint="The traffic type. Examples: organic_social, outreach, email, cpc. Usually auto-filled by a preset.">
            <TextInput value={form.utm_medium} onChange={(e) => set("utm_medium")(e.target.value)} data-testid="link-utm-medium-input" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Field label="UTM campaign" hint="Campaign grouping for analytics. Example: audit_acquisition_2026">
            <TextInput value={form.utm_campaign} onChange={(e) => set("utm_campaign")(e.target.value)} data-testid="link-utm-campaign-input" />
          </Field>
          <Field label="UTM content" hint="Identifies the specific creative/content/variant. Example: board_presentation_post_03">
            <TextInput value={form.utm_content} onChange={(e) => set("utm_content")(e.target.value)} data-testid="link-utm-content-input" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Field label="UTM term" hint="Optional keyword, audience or targeting distinction. Leave blank when not needed. Example: executive_leadership">
            <TextInput value={form.utm_term} onChange={(e) => set("utm_term")(e.target.value)} data-testid="link-utm-term-input" />
          </Field>
          <Field label="UTM id" hint="Optional campaign identifier when intentionally used. Leave blank when not needed.">
            <TextInput value={form.utm_id} onChange={(e) => set("utm_id")(e.target.value)} data-testid="link-utm-id-input" />
          </Field>
        </div>
        <Field label="Generated UTM preview" hint="Preview only — the server generates the real link_id + final values.">
          <p className="bg-paper-deep/50 border border-ink/10 px-3 py-2 font-body text-xs text-ink-soft break-all" data-testid="utm-preview">
            {previewQuery ? `?${previewQuery}` : "(no UTM values set yet)"}
          </p>
        </Field>
      </div>

      {err && <p className="font-body text-sm text-terracotta" data-testid="create-link-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="create-link-submit"
        className="w-full bg-ink text-paper py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors disabled:opacity-50">
        {busy ? "Creating…" : "Create link"}
      </button>
    </form>
  );
}
